import "../locales";
import * as React from "react";
import { Outlet } from "react-router-dom";
import { useUISlice } from "@/models/useUISlice";
import { useToast } from "@/models/useToast";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { getPlatform } from "@/models/platform";
import { useUserSlice } from "@/models/useUserSlice";
import { updateStatusBarStyle } from "@/models/useTheme";
import AlertModal from "@/components/AlertModal";
import AlertPrompt from "@/components/AlertPrompt";
import AlertPromptSignIn from "@/components/AlertPromptSignIn";
import AlertLanguagePicker from "@/components/AlertLanguagePicker";
import AlertWidgetPaywall from "@/components/AlertWidgetPaywall";

const MOBILE_MAX_WIDTH = 768;

// Load analytics with proper error handling
const loadAnalytics = async () => {
  try {
    if (getPlatform() === "web") {
      Promise.all([
        import("@/fetchData/gtag").then((gtag) =>
          gtag.pageview(window.location.pathname)
        ),
        import("@/fetchData/fpixel").then((fbq) => fbq.pageview()),
      ]);
    }
    import("@/fetchData/datadog");
  } catch (error) {
    console.warn("Analytics loading error:", error);
  }
};

export default function MyApp() {
  const { ToastContainer } = useToast();
  const _hasHydrated = useUserSlice((state) => state._hasHydrated);
  const [isInitialized, setIsInitialized] = React.useState(false);

  // Ensure status bar is set after hydration on Android
  React.useEffect(() => {
    if (!_hasHydrated || getPlatform() !== "android") return;

    // Delay to ensure Capacitor bridge is ready
    const timeout = setTimeout(() => {
      updateStatusBarStyle("dark");
    }, 1000);

    return () => clearTimeout(timeout);
  }, [_hasHydrated]);

  React.useEffect(() => {
    let mounted = true;
    let resizeTimeout: NodeJS.Timeout;

    const updateDeviceState = () => {
      if (!mounted) return;

      try {
        const newState = {
          isMobile: window.innerWidth < MOBILE_MAX_WIDTH,
          width: window.innerWidth,
          height: window.innerHeight,
        };

        const currentDevice = useUISlice.getState().device;
        if (
          currentDevice.width !== newState.width ||
          currentDevice.height !== newState.height ||
          currentDevice.isMobile !== newState.isMobile
        ) {
          useUISlice.getState().setDevice(newState);
        }

        if (!isInitialized && mounted) {
          setIsInitialized(true);
        }
      } catch (error) {
        console.error("Failed to update device state:", error);
        if (!isInitialized && mounted) {
          setIsInitialized(true);
        }
      }
    };

    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(updateDeviceState, 100);
    };

    const handlePopState = () => {
      loadAnalytics().catch(console.warn);
    };

    updateDeviceState();

    requestAnimationFrame(() => {
      loadAnalytics().catch(console.warn);
    });

    window.addEventListener("resize", handleResize, { passive: true });
    window.addEventListener("popstate", handlePopState);

    return () => {
      mounted = false;
      clearTimeout(resizeTimeout);
      window.removeEventListener("popstate", handlePopState);
      window.removeEventListener("resize", handleResize);
    };
  }, [isInitialized]);

  const _renderAppContent = () => {
    return (
      <>
        <Outlet />
        <AlertLanguagePicker />
        <AlertPrompt />
        <AlertModal />
        <AlertWidgetPaywall />
        <AlertPromptSignIn />
        <ToastContainer />
      </>
    );
  };

  if (!_hasHydrated) return <div />;

  return (
    <React.Suspense>
      <GoogleOAuthProvider
        clientId={import.meta.env.VITE_PUBLIC_GOOGLE_CLIENT_ID}
      >
        {_renderAppContent()}
      </GoogleOAuthProvider>
    </React.Suspense>
  );
}
